/**
 * Outbound compose HTML: the allowlist, and the plain-text alternative.
 *
 * This is the sending side of ADR 0017. The inbound sanitizer protects *us*
 * from a sender; this one protects *our recipients* from whatever landed in
 * the composer, and protects the sender from mailing out markup they never
 * typed. The two are deliberately not the same list: the inbound policy is
 * broad because it has to render mail other people wrote, and it can afford to
 * be because the result goes into a script-less sandboxed frame under
 * `default-src 'none'`. Outbound HTML has no such container — it is rendered by
 * Outlook, Gmail and a hundred clients we do not control — so the only safe
 * posture is to emit the small set of tags the toolbar can produce and nothing
 * else at all.
 *
 * Three ingress points feed this module, and every one of them is hostile:
 *   - the initial `value`, which for a reply is quoted text derived from a
 *     *received* message;
 *   - paste, which carries whole web pages and Word's namespaced XML;
 *   - drop, which is paste with a different event name.
 * `contenteditable` will happily accept a `<script>` from any of them, so
 * nothing is inserted into the editor that has not been through `sanitizeHtml`
 * first. The editor DOM is therefore always already clean, and what the
 * composer sends is what was on screen.
 *
 * ESLint bans `dangerouslySetInnerHTML` under `src/routes/mail` (ADR 0017 §7).
 * That rule exists to keep *message* HTML out of the app origin; the editor
 * writes its own sanitized output to `el.innerHTML` through a ref, which is the
 * same trust boundary reached from the other side — the bytes have passed this
 * allowlist, and the allowlist is the reason it is allowed.
 *
 * NOTE: a client-side allowlist is a UX and mess control, not a security
 * control — anyone can POST `body_html` directly. The server applies
 * `mail.SanitizeOutgoing` to the same effect before the MIME is built
 * (`backend/internal/mail/sanitize_outgoing.go`); this file and that one are
 * two statements of one policy and are meant to be kept in step.
 */

/**
 * Elements that survive. This is exactly what the toolbar can produce, plus
 * `div` because every `contenteditable` implementation reaches for it.
 *
 * Adding to this list means adding a tag that ships to other people's mail
 * clients, so each addition wants a test, in the spirit of the rule ADR 0017
 * sets for the inbound policy.
 */
const ALLOWED_ELEMENTS = new Set([
  'a',
  'b',
  'blockquote',
  'br',
  'div',
  'em',
  'i',
  'li',
  'ol',
  'p',
  'strong',
  'u',
  'ul',
]);

/**
 * Elements removed *with their contents*, because their contents are not text.
 * Unwrapping any of these would paste the body of a stylesheet or a script into
 * the message as visible prose.
 *
 * `svg` and `math` are here for the reason the attachment lane refuses to serve
 * SVG inline (ADR 0017 §6): SVG is a scriptable document format, and its
 * sanitization is a separate problem from HTML's. It is not embeddable in
 * outgoing mail at all.
 *
 * `img` is here too, and that one is a deliberate product decision rather than
 * a parser one: an image pasted from a web page is a remote `src` on someone
 * else's server. Sending it would make every recipient fetch from that host,
 * turning a paste into a third-party beacon the sender never agreed to — the
 * exact thing ADR 0018 refuses on the reading side. Inline images come back
 * when attachments do, as `cid:` parts we actually own.
 */
const DROPPED_WITH_CONTENTS = new Set([
  'applet',
  'area',
  'audio',
  'base',
  'button',
  'canvas',
  'embed',
  'form',
  'frame',
  'frameset',
  'head',
  'iframe',
  'img',
  'input',
  'link',
  'map',
  'math',
  'meta',
  'noscript',
  'object',
  'option',
  'param',
  'picture',
  'script',
  'select',
  'source',
  'style',
  'svg',
  'template',
  'textarea',
  'title',
  'track',
  'video',
]);

/**
 * Elements that are neither allowed nor poison: their *text* is the user's
 * content and must not be lost, but the tag itself has no place in the output.
 * They are replaced by the block element that keeps the text on its own line,
 * or unwrapped entirely when they are inline.
 *
 * Tables get this treatment rather than being dropped, and that is the whole
 * reason the map exists. A paste from a spreadsheet is common and a table
 * dropped with its contents is silent data loss — the worst failure a composer
 * can have. A row becomes a line and its cells run together on it, which is a
 * visible degradation the user can see and fix before they hit send.
 */
const REPLACED_WITH: Record<string, string> = {
  caption: 'div',
  center: 'div',
  dd: 'div',
  dl: 'div',
  dt: 'div',
  figcaption: 'div',
  figure: 'div',
  h1: 'div',
  h2: 'div',
  h3: 'div',
  h4: 'div',
  h5: 'div',
  h6: 'div',
  header: 'div',
  footer: 'div',
  article: 'div',
  aside: 'div',
  main: 'div',
  nav: 'div',
  pre: 'div',
  section: 'div',
  tr: 'div',
  hr: 'div',
};

/**
 * The one attribute besides `href` that survives, and only with these exact
 * values, on these elements: the class names Gmail puts on a reply's quoted
 * part (`gmail_quote` on the wrapper and the `blockquote`, `gmail_attr` on the
 * "On …, … wrote:" line). Gmail, Apple Mail, Outlook on the web and most
 * webmail fold a quote they recognise behind a "…", which is the whole reason
 * to send them (#181), and the composer finds its own quote by the same
 * names to fold it in the editor (`splitQuote`). A class carries no URL, no
 * script and no style; anything else in the attribute, including a list that
 * merely contains one of these, is dropped like every other attribute.
 * `backend/internal/mail/sanitize_outgoing.go` admits exactly the same.
 */
const QUOTE_CLASSES: Record<string, ReadonlySet<string>> = {
  blockquote: new Set(['gmail_quote']),
  div: new Set(['gmail_quote', 'gmail_attr']),
};

/** Schemes a link may use. The set the inbound policy admits, and no more. */
const ALLOWED_SCHEMES = /^(?:https?|mailto|tel):/i;

/** How deep a nested structure may be before it is flattened. */
const MAX_DEPTH = 40;

/** Blocks that put their content on its own line in the text alternative. */
const BLOCK_ELEMENTS = new Set(['blockquote', 'div', 'li', 'ol', 'p', 'ul']);

/**
 * `sanitizeHtml` returns markup containing only `ALLOWED_ELEMENTS`, with no
 * attribute anywhere except a scheme-checked `href` on `a` and the quote
 * classes of `QUOTE_CLASSES`.
 *
 * Parsing is done with `DOMParser`, which builds an *inert* document: no
 * script runs, no `img` loads, no stylesheet is fetched, and entity and
 * numeric escapes are decoded by the parser before any of this code looks at
 * them. That last part is why the scheme check below can be a plain string
 * test — `java&#115;cript:` has already become `javascript:` by the time it is
 * read, so there is no encoding left to be clever with.
 *
 * The pipeline is run twice and the two outputs compared, exactly as the
 * inbound sanitizer does (ADR 0017 §2). A parser that produces different
 * markup on a second pass over its own output is the mutation-XSS primitive;
 * when that happens here the HTML is abandoned entirely and the input's text
 * content is returned as escaped plain text, which is always safe and always
 * legible.
 */
export function sanitizeHtml(input: string): string {
  const once = sanitizePass(input);
  const twice = sanitizePass(once);
  if (once !== twice) return escapeText(htmlToText(input));
  return once;
}

function sanitizePass(input: string): string {
  const doc = new DOMParser().parseFromString(input, 'text/html');
  const out: string[] = [];
  serializeChildren(doc.body, out, 0);
  // Whitespace at the very start of a document is discarded by the HTML
  // parser but whitespace after an explicit `<body>` tag is not, so a first
  // pass over a pasted full document keeps a leading newline the second pass
  // then drops. That is the parser being consistent with itself, not a
  // mutation, and trimming here is what lets the fixpoint check in
  // `sanitizeHtml` mean what it says rather than firing on every Word paste.
  return collapseEmpty(out.join('')).replace(/^[\t\n\r ]+/, '').replace(/[\t\n\r ]+$/, '');
}

function serializeChildren(parent: Node, out: string[], depth: number): void {
  for (const node of Array.from(parent.childNodes)) {
    serializeNode(node, out, depth);
  }
}

function serializeNode(node: Node, out: string[], depth: number): void {
  if (node.nodeType === 3 /* Node.TEXT_NODE */) {
    out.push(escapeText(node.nodeValue ?? ''));
    return;
  }
  // Comments, CDATA and processing instructions carry no user content and are
  // a known parser-confusion surface; they are dropped without recursion.
  if (node.nodeType !== 1 /* Node.ELEMENT_NODE */) return;

  const el = node as Element;
  const tag = el.localName.toLowerCase();

  // A namespaced element — `o:p` and friends from a Word paste, or anything
  // smuggled in via foreign content — is never in the allowlist, so it is
  // handled by the unwrap path below and only its text survives.
  if (DROPPED_WITH_CONTENTS.has(tag)) return;

  // Past the depth cap the structure is abandoned but the text is not: deeply
  // nested junk is a real paste artefact, not necessarily an attack, and
  // truncating a user's message would be the wrong answer to it.
  if (depth >= MAX_DEPTH) {
    out.push(escapeText(el.textContent ?? ''));
    return;
  }

  const emitted = ALLOWED_ELEMENTS.has(tag) ? tag : REPLACED_WITH[tag];
  if (emitted === undefined) {
    // Unknown or inline-only: unwrap, keeping the children.
    serializeChildren(el, out, depth + 1);
    return;
  }

  if (emitted === 'br') {
    out.push('<br>');
    return;
  }
  // `hr` maps to an empty `div`, which would serialize as a stray blank line;
  // the rule above turned it into a block, so let the collapse pass drop it.

  if (emitted === 'a') {
    const href = safeHref(el.getAttribute('href'));
    if (href === undefined) {
      // A link whose target we refuse is not a reason to lose its words.
      serializeChildren(el, out, depth + 1);
      return;
    }
    out.push(`<a href="${escapeAttr(href)}">`);
    serializeChildren(el, out, depth + 1);
    out.push('</a>');
    return;
  }

  // A class survives only on the element that carried it, never on one a
  // `section` or an `h2` was turned into: those were not a quote.
  const className = emitted === tag ? quoteClass(tag, el.getAttribute('class')) : undefined;
  out.push(className === undefined ? `<${emitted}>` : `<${emitted} class="${className}">`);
  serializeChildren(el, out, depth + 1);
  out.push(`</${emitted}>`);
}

function quoteClass(tag: string, raw: string | null): string | undefined {
  const value = raw?.trim();
  return value !== undefined && QUOTE_CLASSES[tag]?.has(value) ? value : undefined;
}

/**
 * The `href` check. Anything without an explicitly allowed scheme is refused,
 * including relative and protocol-relative URLs: a relative link in a mail
 * message resolves against whatever the recipient's client thinks the base is,
 * which is never what the sender meant.
 *
 * Tab, newline, carriage return and NUL are stripped *before* the scheme is
 * read because browsers strip them too when they resolve a URL — `java\nscript:`
 * is a working `javascript:` URL in a mail client that does its own resolution,
 * and a check that ran on the raw string would not see it.
 *
 * Exported for `./links`, which builds the targets the Link field, the
 * autolinker and URL paste produce, and checks each one here before use.
 */
export function safeHref(raw: string | null): string | undefined {
  if (raw === null) return undefined;
  const cleaned = raw.replaceAll('\u0000', '').replace(/[\t\n\r]/g, '').trim();
  if (cleaned === '') return undefined;
  if (!ALLOWED_SCHEMES.test(cleaned)) return undefined;
  return cleaned;
}

/**
 * Empty blocks the unwrapping leaves behind: `<div></div>` from an `hr`, or a
 * wrapper whose only child was a dropped `img`. An empty `<p>` the user typed
 * is a deliberate blank line, so `p` is exempt.
 */
function collapseEmpty(html: string): string {
  let previous: string;
  let current = html;
  do {
    previous = current;
    current = current.replace(/<(div|blockquote|ul|ol|li|a)(?: class="[a-z_]+")?><\/\1>/g, '');
  } while (current !== previous);
  return current;
}

function escapeText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeAttr(value: string): string {
  return escapeText(value).replace(/"/g, '&quot;');
}

/**
 * The plain-text alternative.
 *
 * A `text/plain` part built by stripping tags is worse than no part at all: it
 * runs every list item and every paragraph into one line, and the recipient
 * whose client prefers text — or whose screen reader reads it — gets a wall.
 * This produces the conventions mail has used for thirty years instead: `- ` on
 * unordered items, `1. ` on ordered ones, `> ` on quoted lines, and the link
 * target spelled out after its text so a text-only reader can still reach it.
 *
 * It runs over the *sanitized* markup in practice, but it is written to survive
 * arbitrary input, since it is also the fallback path when the fixpoint check
 * fails and there is no sanitized markup to run over.
 */
export function htmlToText(input: string): string {
  const doc = new DOMParser().parseFromString(input, 'text/html');
  const lines: TextLine[] = [{ quote: 0, text: '' }];
  walkText(doc.body, lines, { quote: 0, counters: [] });
  return renderLines(lines);
}

/**
 * Text is accumulated as lines carrying their own quote depth rather than as a
 * string with prefixes written into it as they are needed. A `blockquote` has
 * to mark *every* line it covers, including the blank ones between its
 * paragraphs, and a nested quote has to mark them twice — building that by
 * appending prefixes mid-stream means guessing, at each append, whether a line
 * has already started. Deciding the depth per line and rendering once at the
 * end removes the guess.
 */
interface TextLine {
  quote: number;
  text: string;
}

interface TextContext {
  /** Nesting depth of `blockquote`, one `> ` of prefix per level. */
  quote: number;
  /** One entry per open `ol`; `undefined` marks an enclosing `ul`. */
  counters: (number | undefined)[];
}

function walkText(parent: Node, lines: TextLine[], ctx: TextContext): void {
  for (const node of Array.from(parent.childNodes)) {
    if (node.nodeType === 3 /* Node.TEXT_NODE */) {
      // Collapse the source's own indentation the way a renderer would: a
      // newline in the markup is not a newline in the message.
      appendText(lines, (node.nodeValue ?? '').replace(/\s+/g, ' '));
      continue;
    }
    if (node.nodeType !== 1 /* Node.ELEMENT_NODE */) continue;

    const el = node as Element;
    const tag = el.localName.toLowerCase();
    if (DROPPED_WITH_CONTENTS.has(tag)) continue;

    if (tag === 'br') {
      lines.push({ quote: ctx.quote, text: '' });
      continue;
    }

    if (tag === 'a') {
      walkText(el, lines, ctx);
      const href = safeHref(el.getAttribute('href'));
      // `<a href="https://x">https://x</a>` is the shape every autolinker
      // produces; repeating the target after it would only be noise.
      if (href !== undefined && collapse(el.textContent ?? '') !== href) {
        appendText(lines, ` <${href}>`);
      }
      continue;
    }

    if (tag === 'li') {
      const counters = ctx.counters;
      const ordinal = counters.length > 0 ? counters[counters.length - 1] : undefined;
      startLine(lines, ctx.quote);
      if (ordinal === undefined) {
        appendText(lines, '- ');
      } else {
        appendText(lines, `${ordinal}. `);
        counters[counters.length - 1] = ordinal + 1;
      }
      walkText(el, lines, ctx);
      startLine(lines, ctx.quote);
      continue;
    }

    if (tag === 'ol' || tag === 'ul') {
      blankLine(lines, ctx.quote);
      ctx.counters.push(tag === 'ol' ? startOf(el) : undefined);
      walkText(el, lines, ctx);
      ctx.counters.pop();
      blankLine(lines, ctx.quote);
      continue;
    }

    if (tag === 'blockquote') {
      blankLine(lines, ctx.quote);
      ctx.quote += 1;
      walkText(el, lines, ctx);
      ctx.quote -= 1;
      blankLine(lines, ctx.quote);
      continue;
    }

    // A paragraph stands alone with an empty line either side; every other
    // block just starts a new line. `blockquote`, `ol` and `ul` are handled
    // above because they also open a context, but they separate the same way.
    if (tag === 'p') {
      blankLine(lines, ctx.quote);
      walkText(el, lines, ctx);
      blankLine(lines, ctx.quote);
      continue;
    }

    if (BLOCK_ELEMENTS.has(tag) || REPLACED_WITH[tag] !== undefined) {
      startLine(lines, ctx.quote);
      walkText(el, lines, ctx);
      startLine(lines, ctx.quote);
      continue;
    }

    walkText(el, lines, ctx);
  }
}

/** The line text is being appended to; the list is never left empty. */
function lastLine(lines: TextLine[]): TextLine {
  const line = lines[lines.length - 1];
  if (line !== undefined) return line;
  const fresh: TextLine = { quote: 0, text: '' };
  lines.push(fresh);
  return fresh;
}

function appendText(lines: TextLine[], text: string): void {
  const line = lastLine(lines);
  const next = line.text === '' ? text.replace(/^ /, '') : text;
  if (next === '') return;
  line.text += next;
}

/** Ensures the next text lands on a fresh line at the given quote depth. */
function startLine(lines: TextLine[], quote: number): void {
  const line = lastLine(lines);
  if (line.text === '') {
    line.quote = quote;
    return;
  }
  lines.push({ quote, text: '' });
}

/** As `startLine`, plus one empty line of separation before the next text. */
function blankLine(lines: TextLine[], quote: number): void {
  startLine(lines, quote);
  lines.push({ quote, text: '' });
}

/**
 * Runs of empty lines collapse to one and disappear at both ends, so the
 * "blank line either side" rule above can be applied unconditionally by every
 * block without any of them having to know what its neighbours did.
 */
function renderLines(lines: TextLine[]): string {
  const kept: TextLine[] = [];
  for (const line of lines) {
    const text = line.text.replace(/\s+$/, '');
    if (text === '') {
      const previous = kept[kept.length - 1];
      if (previous === undefined) continue;
      if (previous.text === '') {
        // A run of blanks spans the end of one quote level and the start of
        // the next, so the surviving separator takes the shallowest depth in
        // the run: the empty line after a quoted paragraph belongs to the
        // quote, the one after the whole `blockquote` does not.
        previous.quote = Math.min(previous.quote, line.quote);
        continue;
      }
    }
    kept.push({ quote: line.quote, text });
  }
  while (kept.at(-1)?.text === '') kept.pop();
  return kept.flatMap(renderLine).join('\n');
}

/**
 * The width a quoted line is rewrapped to: RFC 3676 §4.2's recommendation, so
 * a reply quoting a reply quoting a reply does not grow a ragged right edge of
 * half-lines as each `> ` pushes the previous wrap past the reader's.
 */
export const QUOTE_WIDTH = 76;

/**
 * One line of the text part, rewrapped at `QUOTE_WIDTH` if it is quoted.
 *
 * Only quoted lines are wrapped. The user's own paragraphs go out as they were
 * typed, one line each, and the reader's client wraps them to its own width;
 * hard breaks in prose are what format=flowed exists to undo, and this part is
 * not sent flowed. A quote is different: every client already expects it
 * hard-wrapped, and it is the part that gets longer on every round.
 *
 * Breaks are only ever made at spaces, so a URL (or a `<https://…>` link
 * target) longer than the width keeps a line of its own rather than being cut
 * in half, which no reader can follow. Every continuation repeats the line's
 * whole prefix -- the `> ` this quote adds *and* any `>` markers the quoted
 * text already carried, from a plain-text original that was itself a reply --
 * so a wrapped line stays at its own depth. Kept safe for a reader that does
 * treat it as flowed: no wrapped line ends in a space (which would mean "join
 * me to the next"), and no continuation starts with a `>` that would read as
 * one more level of quoting.
 */
function renderLine(line: TextLine): string[] {
  const whole = `${'> '.repeat(line.quote)}${line.text}`.replace(/\s+$/, '');
  if (whole.length <= QUOTE_WIDTH || (line.quote === 0 && !line.text.startsWith('>'))) return [whole];
  const marker = /^(?:> ?)+/.exec(line.text)?.[0] ?? '';
  const head = `${'> '.repeat(line.quote)}${marker}${marker === '' || marker.endsWith(' ') ? '' : ' '}`;
  const words = line.text.slice(marker.length).split(/\s+/).filter((word) => word !== '');
  const out: string[] = [];
  let current = '';
  for (const word of words) {
    if (current === '') {
      current = word;
    } else if (head.length + current.length + 1 + word.length <= QUOTE_WIDTH || word.startsWith('>')) {
      current += ` ${word}`;
    } else {
      out.push(head + current);
      current = word;
    }
  }
  if (current !== '') out.push(head + current);
  return out;
}

function startOf(el: Element): number {
  const raw = Number.parseInt(el.getAttribute('start') ?? '', 10);
  return Number.isFinite(raw) && raw > 0 ? raw : 1;
}

function collapse(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

/**
 * True when the markup carries nothing a recipient would see. An editor that
 * has been typed in and then emptied still holds `<br>` or `<div><br></div>`,
 * and sending that as a "non-empty" body would defeat the composer's own
 * "you have not written anything" check.
 */
export function isBlankHtml(html: string): boolean {
  return htmlToText(html) === '';
}
/**
 * Turns seeded plain text into the HTML the editor starts from.
 *
 * A new message's signature, a `mailto:` body, and a popped-out window's draft
 * when the editor never produced HTML all arrive as text and need to become a
 * document before the editor can show them. (A reply does not come through
 * here any more: `buildReplyQuote` builds its quote as HTML from the start.)
 *
 * Deliberately not clever: every line is escaped and becomes its own
 * paragraph, with blank lines preserved as empty ones. Recognising `> ` and
 * rebuilding it as `<blockquote>` would read better, but it would also rewrite
 * the user's quoted text on the way in, and a composer that edits your reply
 * before you have typed in it is worse than one that shows it verbatim.
 */
export function textToHtml(text: string): string {
  if (text === '') return '';
  return text
    .split('\n')
    .map((line) => (line === '' ? '<p><br></p>' : `<p>${escapeText(line)}</p>`))
    .join('');
}

/**
 * The quoted original under a reply, as Gmail builds it (#181):
 *
 *   <div class="gmail_quote">
 *     <div class="gmail_attr">On …, … wrote:</div>
 *     <blockquote class="gmail_quote">…the original…</blockquote>
 *   </div>
 *
 * The classes are what let the recipient's client fold the quote behind a
 * "…" (see `QUOTE_CLASSES`), and the wrapper is what lets this composer fold
 * it in the editor (`splitQuote`). The attribution sits inside the wrapper
 * because it belongs to the quote: folded, a reply should show nothing but
 * what the user wrote.
 *
 * The original's HTML is quoted when it has any, through this module's own
 * allowlist -- which drops every `img`, so the reading pane's rewritten image
 * URLs (the reason the server's prefill re-fetches the original rather than
 * quoting the display copy) never reach the quote, and the server applies
 * `SanitizeOutgoing` again at send. Otherwise its text is, one paragraph per
 * blank-line-separated block and one line per line, so the text part reads
 * as the original did. `>` markers the text already carries are left as they
 * are, for the same reason `textToHtml` leaves them: they are somebody else's
 * words. Neither: no quote at all, only the attribution, rather than an empty
 * box a recipient would wonder about.
 */
export function buildReplyQuote(attribution: string, html: string | undefined, text: string | undefined): string {
  let content = html === undefined || html === '' ? '' : sanitizeHtml(html);
  if (isBlankHtml(content)) content = '';
  if (content === '' && text !== undefined && text.trim() !== '') content = textQuoteHtml(text);
  const attr = escapeText(attribution);
  if (content === '') return `<p>${attr}</p>`;
  return sanitizeHtml(
    `<div class="gmail_quote"><div class="gmail_attr">${attr}</div>` +
      `<blockquote class="gmail_quote">${content}</blockquote></div>`,
  );
}

function textQuoteHtml(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .split(/\n[\t ]*\n/)
    .filter((paragraph) => paragraph.trim() !== '')
    .map((paragraph) => `<p>${paragraph.split('\n').map(escapeText).join('<br>')}</p>`)
    .join('');
}

const QUOTE_OPEN = '<div class="gmail_quote">';

/**
 * A body with its trailing quote set apart: `own` is what the user wrote,
 * `quote` the folded part, and `own + quote` is always the body again, byte
 * for byte -- the composer folds and unfolds by slicing, never by
 * re-serialising, so the editor is handed back exactly the string it emitted
 * and the caret is never thrown to the start of the message.
 *
 * Only a quote at the *end* folds: a top-level `div.gmail_quote` followed by
 * nothing but blank lines. Folding one with the user's writing under it (they
 * answered inline, or wrote below the quote) would hide their words from
 * them, so such a body has no fold and shows whole. A body with no quote --
 * a forward, a new message, a quote the user deleted -- has none either.
 *
 * It reads sanitized markup (the editor's output, or a draft the server
 * sanitized), where a tag is always a tag: text has its `<` escaped and only
 * allowlisted elements remain, so counting open and close tags is exact.
 */
export function splitQuote(html: string): { own: string; quote: string } | undefined {
  const tags = /<(\/?)([a-z]+)[^>]*>/g;
  let depth = 0;
  let start = -1;
  let found: { own: string; quote: string } | undefined;
  for (let match = tags.exec(html); match !== null; match = tags.exec(html)) {
    const [whole, closing, name] = match;
    if (name === 'br') continue;
    if (closing === '') {
      if (depth === 0 && whole === QUOTE_OPEN) start = match.index;
      depth += 1;
    } else {
      depth -= 1;
      if (depth === 0 && start >= 0) {
        const end = match.index + whole.length;
        found = isBlankHtml(html.slice(end)) ? { own: html.slice(0, start), quote: html.slice(start) } : undefined;
        start = -1;
      }
    }
  }
  return found;
}
